/**
 * EtfContributionDialog — the Leave-One-Out scenario of ONE ETF, in full.
 *
 * Prima vs Senza questo ETF: renormalised weights, Diversification Score with
 * its comparability and calibration, the five families with their status
 * transitions, concentration, duplication with its relief and the exposures
 * that change. Every value is read from the R1.0 payload the panel already has:
 * this component formats, it does not compute.
 */
import { AlertTriangle, ArrowRight } from "lucide-react";
import {
  Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const num = (value, digits = 1) =>
  value === null || value === undefined ? "n/d" : Number(value).toFixed(digits);
const pct = (value, digits = 1) =>
  value === null || value === undefined ? "n/d" : `${num(value, digits)}%`;
const signed = (value, digits = 2) => {
  if (value === null || value === undefined) return "n/d";
  if (Number(value) === 0) return num(0, digits);
  return `${Number(value) > 0 ? "+" : "−"}${num(Math.abs(value), digits)}`;
};
const scoreValue = (value) =>
  value === null || value === undefined ? "n/d" : num(value, 0);

const EXPOSURE_ROWS = 20;

const CHANGE_LABEL = {
  increased: { label: "aumenta", variant: "secondary" },
  decreased: { label: "diminuisce", variant: "secondary" },
  removed: { label: "scompare", variant: "danger" },
  introduced: { label: "nuova", variant: "warning" },
};

const STATUS_LABEL = {
  ok: "misurata",
  supported: "misurata",
  not_supported: "non misurabile",
  not_applicable: "N/A",
  not_applicable_single_asset: "N/A (portafoglio mono-asset)",
  insufficient: "dati insufficienti",
  no_data: "nessun dato",
};

const statusText = (status) => STATUS_LABEL[status] || status || "n/d";

export function EtfContributionDialog({
  open, onOpenChange, etf, dimensionLabel, analysis,
}) {
  const cmp = etf?.score || {};
  const weightsAfter = Object.entries(etf?.weights_after || {});
  const metrics = [...(etf?.concentration || []), ...(etf?.duplication || [])];
  const exposures = etf?.exposure_changes || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[calc(100vw-2rem)] sm:max-w-3xl bg-surface border-line text-content"
        data-testid="contribution-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-base" data-testid="contribution-dialog-title">
            {etf ? `Senza ${etf.label}` : "Dettaglio del contributo"}
          </DialogTitle>
          <DialogDescription className="text-xs text-content-muted">
            Scenario read-only: l&apos;ETF viene rimosso e il suo peso redistribuito in
            proporzione fra gli altri. Lettura strutturale su {(dimensionLabel || "").toLowerCase()}.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="px-6 pb-6 space-y-5">
          {etf && (
            <>
              <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
                <div>
                  <p className="text-label text-content-muted">Peso rimosso</p>
                  <p
                    className="text-2xl font-semibold tabular text-content leading-none"
                    data-testid="contribution-dialog-weight"
                  >
                    {pct(etf.weight_pct, 1)}
                  </p>
                </div>
                <div>
                  <p className="text-label text-content-muted">Diversification Score</p>
                  <p
                    className="text-2xl font-semibold tabular text-content leading-none"
                    data-testid="contribution-dialog-score"
                  >
                    {scoreValue(cmp.score_before)}
                    <ArrowRight className="inline w-4 h-4 mx-2 text-content-muted" aria-hidden="true" />
                    <span className="sr-only"> senza questo ETF </span>
                    {scoreValue(cmp.score_after)}
                  </p>
                </div>
                <div>
                  <p className="text-label text-content-muted">Contributo marginale</p>
                  <p
                    className="text-2xl font-semibold tabular text-content leading-none"
                    data-testid="contribution-dialog-mdc"
                  >
                    {etf.marginal_diversification_contribution === null
                    || etf.marginal_diversification_contribution === undefined
                      ? "non confrontabile"
                      : `${signed(etf.marginal_diversification_contribution, 1)} punti`}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge
                    variant={cmp.comparable ? "secondary" : "warning"}
                    data-testid="contribution-dialog-comparable"
                  >
                    {cmp.comparable ? "confrontabile" : "non confrontabile"}
                  </Badge>
                  <Badge variant="secondary" data-testid="contribution-dialog-calibration">
                    Calibrazione {cmp.calibration_after || analysis?.calibration_id || "n/d"}
                  </Badge>
                </div>
              </div>
              <p className="text-label text-content-muted" data-testid="contribution-dialog-reason">
                {cmp.reason || etf.reason}
              </p>

              <div>
                <h3 className="text-sm font-semibold text-content mb-2">
                  Come vengono rinormalizzati gli altri ETF
                </h3>
                <div className="flex flex-wrap gap-2" data-testid="contribution-dialog-weights">
                  {weightsAfter.map(([key, value]) => (
                    <Badge
                      key={key}
                      variant="outline"
                      data-testid={`contribution-dialog-weight-${key}`}
                    >
                      {key}: {pct(value, 2)}
                    </Badge>
                  ))}
                </div>
                <p className="text-label text-content-muted mt-2">
                  Ogni peso residuo è il peso originale diviso per la quota che resta:
                  le proporzioni relative fra gli ETF rimasti non cambiano e il totale
                  torna al 100%.
                </p>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <h3 className="text-sm font-semibold text-content mb-2">
                    Famiglie del Diversification Score
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="contribution-families">
                      <thead className="text-label text-content-muted">
                        <tr className="border-b border-border">
                          <th scope="col" className="px-3 py-2 text-left font-medium">Famiglia</th>
                          <th scope="col" className="px-3 py-2 text-right font-medium">Prima</th>
                          <th scope="col" className="px-3 py-2 text-right font-medium">Dopo</th>
                          <th scope="col" className="px-3 py-2 text-right font-medium">Δ</th>
                          <th scope="col" className="px-3 py-2 text-left font-medium">Stato</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(etf.families || []).map((family) => (
                          <tr
                            key={family.family}
                            className="border-b border-border/60 last:border-0"
                            data-testid={`contribution-family-${family.family}`}
                          >
                            <td className="px-3 py-2 text-content">{family.label}</td>
                            <td className="px-3 py-2 text-right tabular text-content-secondary">
                              {family.subscore_before === null ? "N/A" : num(family.subscore_before, 0)}
                            </td>
                            <td className="px-3 py-2 text-right tabular text-content-secondary">
                              {family.subscore_after === null ? "N/A" : num(family.subscore_after, 0)}
                            </td>
                            <td className="px-3 py-2 text-right tabular text-content">
                              {family.delta === null || family.delta === undefined
                                ? "n/d" : signed(family.delta, 1)}
                            </td>
                            <td className="px-3 py-2 text-label text-content-muted">
                              {statusText(family.status_before)}
                              {" → "}
                              {statusText(family.status_after)}
                              {family.note ? ` · ${family.note}` : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-label text-content-muted mt-2">
                    Quando una famiglia passa da misurata a N/A il delta non viene
                    inventato: resta n/d e lo stato spiega perché.
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-content mb-2">
                    Concentrazione e duplicazione
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="contribution-metrics">
                      <thead className="text-label text-content-muted">
                        <tr className="border-b border-border">
                          <th scope="col" className="px-3 py-2 text-left font-medium">Metrica</th>
                          <th scope="col" className="px-3 py-2 text-right font-medium">Prima</th>
                          <th scope="col" className="px-3 py-2 text-right font-medium">Dopo</th>
                          <th scope="col" className="px-3 py-2 text-right font-medium">Δ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.map((metric) => (
                          <tr
                            key={metric.metric}
                            className="border-b border-border/60 last:border-0"
                            data-testid={`contribution-metric-${metric.metric}`}
                          >
                            <td className="px-3 py-2 text-content">{metric.label}</td>
                            <td className="px-3 py-2 text-right tabular text-content-secondary">
                              {metric.unit === "pct_points" ? pct(metric.before, 1) : num(metric.before, 2)}
                            </td>
                            <td className="px-3 py-2 text-right tabular text-content-secondary">
                              {metric.unit === "pct_points" ? pct(metric.after, 1) : num(metric.after, 2)}
                            </td>
                            <td className="px-3 py-2 text-right tabular text-content">
                              {signed(metric.delta, 2)}
                              {metric.unit === "pct_points" && metric.delta !== null ? " pp" : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-label text-content-muted mt-2" data-testid="contribution-relief">
                    Duplication Relief: {signed(etf.duplication_relief_pct, 2)} pp. Non è una
                    misura di rischio e non è additivo fra ETF: può muoversi in direzione
                    diversa dal contributo allo score.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-content mb-2">
                  Esposizioni che cambiano ({(dimensionLabel || "").toLowerCase()})
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="contribution-exposures">
                    <thead className="text-label text-content-muted">
                      <tr className="border-b border-border">
                        <th scope="col" className="px-3 py-2 text-left font-medium">Esposizione</th>
                        <th scope="col" className="px-3 py-2 text-left font-medium">Effetto</th>
                        <th scope="col" className="px-3 py-2 text-right font-medium">Prima</th>
                        <th scope="col" className="px-3 py-2 text-right font-medium">Dopo</th>
                        <th scope="col" className="px-3 py-2 text-right font-medium">Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exposures.slice(0, EXPOSURE_ROWS).map((change) => (
                        <tr
                          key={change.key}
                          className="border-b border-border/60 last:border-0"
                          data-testid={`contribution-exposure-${change.key}`}
                        >
                          <td className="px-3 py-2 text-content">{change.label}</td>
                          <td className="px-3 py-2">
                            <Badge variant={CHANGE_LABEL[change.change]?.variant || "secondary"}>
                              {CHANGE_LABEL[change.change]?.label || change.change}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-right tabular text-content-secondary">
                            {pct(change.before_pct, 2)}
                          </td>
                          <td className="px-3 py-2 text-right tabular text-content-secondary">
                            {pct(change.after_pct, 2)}
                          </td>
                          <td className="px-3 py-2 text-right tabular text-content">
                            {signed(change.delta_pct, 2)} pp
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(etf.exposure_changes_truncated
                  || exposures.length > EXPOSURE_ROWS) && (
                  <p
                    className="text-label text-content-muted mt-2"
                    data-testid="contribution-exposures-truncated"
                  >
                    Mostrate {Math.min(exposures.length, EXPOSURE_ROWS)} variazioni
                    {exposures.length > EXPOSURE_ROWS
                      ? ` delle ${exposures.length} pubblicate` : ""}
                    {etf.exposure_changes_truncated
                      ? ` su ${etf.exposure_changes_truncated.total} totali; le non pubblicate valgono ${pct(etf.exposure_changes_truncated.hidden_contribution_pct, 2)} in totale`
                      : ""}
                    . La massa non classificata resta una metrica separata e non
                    diventa un&apos;esposizione.
                  </p>
                )}
                <p className="text-label text-content-muted mt-2">
                  Una rimozione non può creare esposizioni nuove: quelle che salgono lo
                  fanno solo per effetto della rinormalizzazione dei pesi.
                </p>
              </div>

              <div className="space-y-2">
                {(etf.flags || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {(etf.flags || []).map((flag) => (
                      <Badge key={flag} variant="warning" data-testid={`contribution-dialog-flag-${flag}`}>
                        {flag === "score_delta_not_available"
                          ? "delta dello score non pubblicabile"
                          : flag === "overlap_not_supported"
                            ? "overlap non misurabile"
                            : flag === "etf_coverage_limited"
                              ? "dati dell'ETF limitati"
                              : flag === "wide_bracket"
                                ? "intervallo ampio"
                                : flag}
                      </Badge>
                    ))}
                  </div>
                )}
                {(etf.warnings || []).map((warning) => (
                  <p
                    key={warning}
                    className="flex items-start gap-2 text-label text-content-muted"
                    data-testid="contribution-dialog-warning"
                  >
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                    <span>{warning}</span>
                  </p>
                ))}
              </div>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export default EtfContributionDialog;
